/**
 * Invoice entity-role resolution.
 * ------------------------------------------------------------------
 * Deterministic, dependency-free classification of every business /
 * person entity printed on a submitted invoice into an explicit ROLE
 * (billing vendor, service provider, customer, vehicle owner, insured,
 * carrier, tow origin, tow destination, …).
 *
 * WHY THIS EXISTS
 * OCR "supplier_name" style fields are unreliable on towing invoices:
 * the customer / vehicle-owner block frequently sits above the issuer
 * block, so a naive `supplier_name` mapping can research the WRONG
 * company (e.g. selecting the vehicle owner "Plastic Materials
 * Trucking" instead of the billing vendor "Flag City Towing and
 * Recovery"). Vendor Intelligence must always investigate the billing
 * vendor / service provider — never the customer, owner, insured,
 * carrier or destination.
 *
 * This module is pure: no I/O, no AI, no randomness. Same text in →
 * same roles out. It never fabricates an entity that is not present in
 * the source text.
 */

export type EntityRoleConfidence = "high" | "medium" | "low";

export interface ResolvedEntity {
  /** Cleaned display name. */
  name: string;
  /** Verbatim source line the value was read from. */
  sourceText: string;
  confidence: EntityRoleConfidence;
  /** Short machine-readable provenance, e.g. "label:owner". */
  source: string;
}

export interface InvoiceEntityRoles {
  billingVendor: ResolvedEntity | null;
  serviceProvider: ResolvedEntity | null;
  invoiceIssuer: ResolvedEntity | null;
  customer: ResolvedEntity | null;
  vehicleOwner: ResolvedEntity | null;
  insured: ResolvedEntity | null;
  carrier: ResolvedEntity | null;
  driver: ResolvedEntity | null;
  towOrigin: string | null;
  towDestination: string | null;
  billingAddress: string | null;
  serviceLocation: string | null;
  vendorPhone: string | null;
  vendorEmail: string | null;
  vendorWebsite: string | null;
  /** Every company name seen, with the role it was classified as. */
  conflicts: Array<{ name: string; role: string }>;
  /** True when the vendor role should be confirmed by a human. */
  needsVendorConfirmation: boolean;
  /** Non-fatal notes for the audit trail. */
  notes: string[];
}

export interface ResolveInvoiceEntitiesInput {
  /** Raw OCR text of the invoice (all pages concatenated). */
  rawText?: string | null;
  /** Vendor name proposed by OCR / structured extraction. */
  ocrVendorName?: string | null;
  /** Vendor value supplied by the customer at intake. */
  customerSuppliedVendor?: string | null;
  /** Address proposed by OCR, used only as a fallback. */
  ocrServiceAddress?: string | null;
}

/* ───────────────────────── text helpers ───────────────────────── */

const BUSINESS_TOKENS =
  /(towing|tow\b|wrecker|recovery|hauling|transport|trucking|storage|impound|garage|auto|automotive|body\s*shop|service[s]?|llc|l\.l\.c|inc\b|incorporated|corp\b|corporation|co\b|company|ltd\b|enterprises|holdings)/i;

const TOWING_TOKENS = /(towing|tow\b|wrecker|recovery|impound|storage|roadside|rotator)/i;

/**
 * Equipment, vehicle and service-line vocabulary. A charge description such as
 * "Recovery Van" or "Rotator" shares tokens with real company names, so those
 * lines must never be promoted to the vendor identity unless the line also
 * carries a real business/corporate marker.
 */
const EQUIPMENT_TOKENS =
  /\b(van|rotator|flat\s*bed|flatbed|roll\s*back|rollback|wheel[-\s]?lift|dolly|dollies|winch(?:ing)?|skid[-\s]?steer|loader|forklift|trailer|dump\s*truck|semi|tractor|air\s*cushion|clean[-\s]?up|cleanup|labor(?:er)?|supervisor|mileage|fuel|decking|tarp|straps?|crane|boom|light\s*tower|pickup|truck|unit|hook[-\s]?up|standby|per\s*mile|per\s*hour|hourly|daily)\b/i;

/** Real business / corporate markers that outrank the equipment vocabulary. */
const BUSINESS_MARKER =
  /\b(llc|l\.l\.c|inc|incorporated|corp|corporation|company|ltd|enterprises|holdings|services|towing|wrecker|garage|automotive|auto\s*body|body\s*shop|transport|trucking|hauling|impound|storage)\b/i;

/**
 * True when a line reads as an invoice charge / equipment description rather
 * than a business name. Exported so downstream consumers can reject a vendor
 * value that an earlier extraction already stored incorrectly.
 */
export function looksLikeEquipmentDescription(line: string): boolean {
  const c = clean(line);
  if (!c) return false;
  if (BUSINESS_MARKER.test(c)) return false;
  return EQUIPMENT_TOKENS.test(c);
}

/** Labels whose value must NEVER become the vendor. */
const NON_VENDOR_LABELS = [
  "owner",
  "vehicle owner",
  "registered owner",
  "customer",
  "customer name",
  "bill to",
  "billed to",
  "bill-to",
  "insured",
  "member",
  "member name",
  "driver",
  "carrier",
  "destination",
  "deliver to",
  "ship to",
  "consignee",
] as const;

/** Labels that explicitly name the vendor / provider. */
const VENDOR_LABELS = [
  "service provider",
  "provider",
  "vendor",
  "vendor name",
  "tow company",
  "towing company",
  "remit to",
  "remit-to",
  "pay to",
  "payable to",
  "company",
  "from",
] as const;

function clean(v: string): string {
  return v
    .replace(/[\s\u00a0]+/g, " ")
    .replace(/^[-–—:;,.\s]+/, "")
    .replace(/[-–—:;,.\s]+$/, "")
    .trim();
}

function norm(v: string): string {
  return clean(v)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\b(llc|inc|co|corp|corporation|company|ltd)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function looksLikeCompany(line: string): boolean {
  const c = clean(line);
  if (c.length < 4 || c.length > 70) return false;
  if (/^\$?[\d.,]+$/.test(c)) return false; // pure numbers / money
  if (/@|https?:\/\//i.test(c)) return false;
  if (/^\d/.test(c)) return false; // street lines
  if (/(invoice|subtotal|total|tax|qty|rate|amount|page\s*\d|date|terms|due)/i.test(c)) return false;
  if (looksLikeEquipmentDescription(c)) return false; // "Recovery Van", "Rotator", …
  return BUSINESS_TOKENS.test(c);
}

const PHONE_RE = /(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/;
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
const URL_RE = /\b(?:https?:\/\/)?(?:www\.)?([a-z0-9-]+\.(?:com|net|org|us|biz))\b/i;

/** Lines that are themselves OCR labels, never values. */
const LABEL_NOISE =
  /^(qty|service|rate|amount|adjustment|total|subtotal|tax|page|date|terms|reason|vin|tag|odometer|ticket|vehicle|member|invoice|due|received|requested|arrive|leave|destination|start|owner|customer|insured|driver|carrier|from|to|for|p\.?o\.?)\b/i;

/** A plausible person/company name value. */
function looksLikeNameValue(v: string): boolean {
  const c = clean(v);
  if (c.length < 3 || c.length > 70) return false;
  if (/[!@$%*]|https?:\/\//.test(c)) return false;
  if (/^\$?[\d.,#]+$/.test(c)) return false;
  if (LABEL_NOISE.test(c)) return false;
  if (!/^[A-Z0-9]/.test(c)) return false;
  return /[A-Za-z]{3}/.test(c);
}

/** First value for a label: same line after ":" or the next usable line. */
function readLabel(lines: string[], label: string): { value: string; sourceText: string } | null {
  const re = new RegExp(
    `^\\s*${label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*(?:#|name)?\\s*(?::|$)\\s*(.*)$`,
    "i",
  );
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(re);
    if (!m) continue;
    const inline = clean(m[1] ?? "");
    if (looksLikeNameValue(inline)) {
      return { value: inline, sourceText: clean(lines[i]) };
    }
    // OCR frequently pushes the value onto a following line, sometimes past
    // one or more sibling labels ("Owner:" → "Ticket #:" → real value).
    for (let j = i + 1; j < Math.min(i + 5, lines.length); j++) {
      const nxt = clean(lines[j]);
      if (!nxt) continue;
      if (!looksLikeNameValue(nxt)) continue;
      return { value: nxt, sourceText: `${clean(lines[i])} ${nxt}`.trim() };
    }
  }
  return null;
}

/** Address-style value for a label ("From:", "To:"), which may be lowercase. */
function readAddressLabel(lines: string[], label: string): string | null {
  const re = new RegExp(`^\\s*${label}\\s*(?::|$)\\s*(.*)$`, "i");
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(re);
    if (!m) continue;
    const candidate = clean(m[1] ?? "");
    const isAddress = (v: string) =>
      v.length > 6 && /[A-Za-z]{3}/.test(v) && /\d|\bave\b|\bst\b|\brd\b|\bct\b|\bhwy\b/i.test(v);
    if (isAddress(candidate)) return candidate;
    for (let j = i + 1; j < Math.min(i + 4, lines.length); j++) {
      const nxt = clean(lines[j]);
      if (!nxt || LABEL_NOISE.test(nxt)) continue;
      if (isAddress(nxt)) return nxt;
    }
  }
  return null;
}

/**
 * Stacked "From:" / "To:" label pairs (common OCR layout where both labels
 * are emitted before both address values). Returns [origin, destination].
 */
function readFromToPair(lines: string[]): [string | null, string | null] {
  const isAddress = (v: string) =>
    v.length > 6 && /[A-Za-z]{3}/.test(v) && /\d|\bave\b|\bst\b|\brd\b|\bct\b|\bhwy\b/i.test(v);
  for (let i = 0; i < lines.length; i++) {
    if (!/^\s*from\s*:?\s*$/i.test(lines[i])) continue;
    let j = i + 1;
    while (j < lines.length && !clean(lines[j])) j++;
    if (j >= lines.length || !/^\s*to\s*:?\s*$/i.test(lines[j])) continue;
    const found: string[] = [];
    for (let k = j + 1; k < Math.min(j + 8, lines.length) && found.length < 2; k++) {
      const v = clean(lines[k]);
      if (v && isAddress(v)) found.push(v);
    }
    if (found.length === 2) return [found[0], found[1]];
  }
  return [null, null];
}

/* ───────────────────────── main resolver ───────────────────────── */

export function resolveInvoiceEntities(
  input: ResolveInvoiceEntitiesInput,
): InvoiceEntityRoles {
  const raw = typeof input.rawText === "string" ? input.rawText : "";
  const lines = raw.split(/\r?\n/);
  const notes: string[] = [];
  const conflicts: Array<{ name: string; role: string }> = [];

  // ── 1. Labeled non-vendor roles (owner, customer, insured, …) ──
  const labeled = new Map<string, { value: string; sourceText: string }>();
  for (const label of NON_VENDOR_LABELS) {
    const hit = readLabel(lines, label);
    if (hit) labeled.set(label, hit);
  }

  const entity = (
    hit: { value: string; sourceText: string } | undefined,
    source: string,
    confidence: EntityRoleConfidence = "high",
  ): ResolvedEntity | null =>
    hit ? { name: clean(hit.value), sourceText: hit.sourceText, confidence, source } : null;

  const vehicleOwner = entity(
    labeled.get("owner") ?? labeled.get("vehicle owner") ?? labeled.get("registered owner"),
    "label:owner",
  );
  let customerEntity =
    entity(
      labeled.get("customer") ??
        labeled.get("customer name") ??
        labeled.get("bill to") ??
        labeled.get("billed to") ??
        labeled.get("bill-to"),
      "label:customer",
    ) ?? vehicleOwner;
  const insured = entity(labeled.get("insured") ?? labeled.get("member"), "label:insured");
  const carrier = entity(labeled.get("carrier"), "label:carrier");
  const driver = entity(labeled.get("driver"), "label:driver");

  // Names that may never be selected as vendor.
  const excluded = new Set<string>();
  for (const e of [vehicleOwner, customerEntity, insured, carrier, driver]) {
    if (e?.name) excluded.add(norm(e.name));
  }
  for (const label of ["destination", "deliver to", "ship to", "consignee"] as const) {
    const hit = labeled.get(label);
    if (hit) excluded.add(norm(hit.value));
  }

  // ── 2. Explicit vendor labels ──
  let vendor: ResolvedEntity | null = null;
  for (const label of VENDOR_LABELS) {
    const hit = readLabel(lines, label);
    if (!hit) continue;
    const candidate = clean(hit.value);
    if (!candidate || excluded.has(norm(candidate))) continue;
    if (!looksLikeCompany(candidate)) continue;
    vendor = { name: candidate, sourceText: hit.sourceText, confidence: "high", source: `label:${label}` };
    break;
  }

  // ── 3. Invoice issuer block (company adjacent to phone / email / logo) ──
  const emailLineIdx = lines.findIndex((l) => EMAIL_RE.test(l));
  const emailMatch = raw.match(EMAIL_RE)?.[0] ?? null;
  const urlFromEmail = emailMatch ? emailMatch.split("@")[1] : null;
  const urlCandidate = raw.match(URL_RE)?.[1] ?? null;
  const websiteMatch =
    urlFromEmail ??
    (urlCandidate && urlCandidate.replace(/\.(com|net|org|us|biz)$/i, "").length >= 5
      ? urlCandidate
      : null);
  const emailDomainTokens = (urlFromEmail ?? "")
    .replace(/\.(com|net|org|us|biz)$/i, "")
    .split(/[^a-z0-9]+/i)
    .filter((t) => t.length > 3)
    .map((t) => t.toLowerCase());

  let issuer: ResolvedEntity | null = null;
  let issuerIdx = -1;
  let bestScore = 0;
  for (let i = 0; i < lines.length; i++) {
    const candidate = clean(lines[i]);
    if (!looksLikeCompany(candidate)) continue;
    if (excluded.has(norm(candidate))) {
      conflicts.push({ name: candidate, role: "excluded (customer/owner/insured/carrier)" });
      continue;
    }
    let score = 1;
    const window = lines.slice(Math.max(0, i - 2), i + 6).join("\n");
    if (PHONE_RE.test(window)) score += 3;
    if (EMAIL_RE.test(window)) score += 3;
    if (/invoice\s*#/i.test(window)) score += 2;
    if (TOWING_TOKENS.test(candidate)) score += 2;
    const cn = norm(candidate);
    if (emailDomainTokens.some((t) => cn.replace(/\s+/g, "").includes(t))) score += 3;
    if (emailLineIdx >= 0 && Math.abs(emailLineIdx - i) <= 6) score += 1;
    if (score > bestScore) {
      bestScore = score;
      issuerIdx = i;
      issuer = {
        name: candidate,
        sourceText: candidate,
        confidence: score >= 6 ? "high" : score >= 4 ? "medium" : "low",
        source: "invoice-header-block",
      };
    }
  }

  if (!vendor && issuer) vendor = issuer;

  // ── 4. Customer-supplied / OCR-proposed value (lowest structured tier) ──
  const ocrVendor = clean(input.ocrVendorName ?? "");
  const supplied = clean(input.customerSuppliedVendor ?? "");
  if (ocrVendor && excluded.has(norm(ocrVendor))) {
    notes.push(
      `OCR proposed "${ocrVendor}" as vendor, but that entity is labeled as customer/owner on the invoice; it was reclassified and never used as the vendor.`,
    );
  }
  if (!vendor) {
    const fallback = [ocrVendor, supplied].find((v) => v && !excluded.has(norm(v)));
    if (fallback) {
      vendor = {
        name: fallback,
        sourceText: fallback,
        confidence: "low",
        source: fallback === ocrVendor ? "ocr-field" : "customer-supplied",
      };
    }
  }

  // ── 5. Vendor contact block, read from the issuer's own lines ──
  let vendorPhone: string | null = null;
  let vendorEmail: string | null = null;
  let billingAddress: string | null = null;
  if (issuerIdx >= 0) {
    const block = lines.slice(issuerIdx + 1, issuerIdx + 8).map(clean);
    const addrParts: string[] = [];
    for (const l of block) {
      if (!l) continue;
      if (!vendorEmail && EMAIL_RE.test(l)) vendorEmail = l.match(EMAIL_RE)![0];
      else if (!vendorPhone && PHONE_RE.test(l)) vendorPhone = l.match(PHONE_RE)![0];
      else if (addrParts.length < 2 && /^\d+\s+\S|,\s*[A-Z]{2}\s*\d{5}/.test(l)) addrParts.push(l);
    }
    if (addrParts.length) billingAddress = addrParts.join(", ");
  }
  vendorPhone = vendorPhone ?? raw.match(PHONE_RE)?.[0] ?? null;
  vendorEmail = vendorEmail ?? emailMatch;

  // ── 6. Tow origin / destination (address values, not names) ──
  const [pairOrigin, pairDestination] = readFromToPair(lines);
  const towOrigin = pairOrigin ?? readAddressLabel(lines, "from");
  const towDestination = pairDestination ?? readAddressLabel(lines, "to");


  // ── 7. Conflict / confirmation bookkeeping ──
  const vendorNorm = vendor ? norm(vendor.name) : null;
  if (
    customerEntity &&
    vendorNorm &&
    (norm(customerEntity.name) === vendorNorm ||
      vendorNorm.includes(norm(customerEntity.name)) ||
      norm(customerEntity.name).includes(vendorNorm))
  ) {
    notes.push(
      `"${customerEntity.name}" matched the invoice issuer and was not treated as the customer.`,
    );
    customerEntity = vehicleOwner;
  }

  if (vendor) conflicts.unshift({ name: vendor.name, role: "invoice issuer / service provider" });
  if (vehicleOwner) conflicts.push({ name: vehicleOwner.name, role: "vehicle owner" });
  if (customerEntity && customerEntity !== vehicleOwner)
    conflicts.push({ name: customerEntity.name, role: "customer" });
  if (insured) conflicts.push({ name: insured.name, role: "insured" });
  if (carrier) conflicts.push({ name: carrier.name, role: "carrier" });

  // Independent corroboration: when OCR's own supplier candidate agrees with
  // the header-block issuer we selected, the identity is no longer weak.
  if (
    vendor &&
    vendor.confidence === "low" &&
    input.ocrVendorName &&
    norm(input.ocrVendorName) === norm(vendor.name)
  ) {
    vendor = { ...vendor, confidence: "medium" };
  }

  const needsVendorConfirmation = !vendor || vendor.confidence === "low";
  if (!vendor) notes.push("No identifiable service provider was found in the submitted documents.");

  return {
    billingVendor: vendor,
    serviceProvider: vendor,
    invoiceIssuer: issuer ?? vendor,
    customer: customerEntity,
    vehicleOwner,
    insured,
    carrier,
    driver,
    towOrigin,
    towDestination,
    billingAddress: billingAddress ?? null,
    serviceLocation: billingAddress ?? (clean(input.ocrServiceAddress ?? "") || null),
    vendorPhone,
    vendorEmail,
    vendorWebsite: websiteMatch,
    conflicts: dedupeConflicts(conflicts),
    needsVendorConfirmation,
    notes,
  };
}

function dedupeConflicts(
  list: Array<{ name: string; role: string }>,
): Array<{ name: string; role: string }> {
  const seen = new Set<string>();
  const out: Array<{ name: string; role: string }> = [];
  for (const c of list) {
    const k = `${norm(c.name)}|${c.role}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(c);
  }
  return out;
}

/**
 * Vendor identity used by every downstream surface (Vendor Intelligence,
 * SOS / licensing / BBB lookups, report rendering). Prefers the resolved
 * billing vendor and NEVER falls back to a customer/owner entity.
 */
export function selectBillingVendor(roles: InvoiceEntityRoles): string | null {
  return roles.billingVendor?.name ?? null;
}

/** Chapter-status model for Vendor Intelligence. */
export type VendorEvidenceStatus =
  | "verified_external_records"
  | "invoice_derived_identity"
  | "limited_external_evidence"
  | "no_vendor_identity";

export const VENDOR_EVIDENCE_STATUS_LABEL: Record<VendorEvidenceStatus, string> = {
  verified_external_records: "Verified External Records",
  invoice_derived_identity: "Invoice-Derived Vendor Identity",
  limited_external_evidence: "Limited External Evidence",
  no_vendor_identity: "No Vendor Identity",
};

export const VENDOR_EVIDENCE_STATUS_MESSAGE: Record<VendorEvidenceStatus, string> = {
  verified_external_records: "External vendor records were independently verified.",
  invoice_derived_identity:
    "Vendor identity was established from the submitted invoice. No independently verified external records were identified from the sources available during this review.",
  limited_external_evidence:
    "Some external sources were checked, but sufficient verified records were not returned.",
  no_vendor_identity:
    "The submitted documents provide no identifiable service provider, so no vendor investigation could be performed.",
};

export function resolveVendorEvidenceStatus(input: {
  hasVendorName: boolean;
  hasVerifiedExternalRecord: boolean;
  externalSourcesChecked: boolean;
}): VendorEvidenceStatus {
  if (input.hasVerifiedExternalRecord) return "verified_external_records";
  if (!input.hasVendorName) return "no_vendor_identity";
  if (input.externalSourcesChecked) return "limited_external_evidence";
  return "invoice_derived_identity";
}
